import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { exhaustMap, of } from 'rxjs';

import {
  catchError,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import {
  getAutomaticAdviceCustomersPreferencesLastUpdate,
  getAutomaticAdviceCustomersPreferencesLastUpdateError,
  getAutomaticAdviceCustomersPreferencesLastUpdateSuccess,
  getAutomaticFlowSchedulerEnabledState,
  getAutomaticFlowSchedulerEnabledStateError,
  getAutomaticFlowSchedulerEnabledStateSuccess,
  resetAutomaticAdviceForAllCustomers,
  resetAutomaticAdviceForAllCustomersError,
  resetAutomaticAdviceForAllCustomersSuccess,
  setAutomaticAdviceCustomersPreferences,
  setAutomaticAdviceCustomersPreferencesError,
  setAutomaticAdviceCustomersPreferencesSuccess,
  setAutomaticFlowEnabledState,
  setAutomaticFlowEnabledStateError,
  setAutomaticFlowEnabledStateSuccess,
  showRecalculateBbaAdviceModal,
  triggerAutomaticFlow,
  triggerAutomaticFlowError,
  triggerAutomaticFlowSuccess,
  triggerRecalculateBbaAdvice,
  triggerRecalculateBbaAdviceError,
  triggerRecalculateBbaAdviceSuccess,
  uploadCompanySettingsFromCsv,
  uploadCompanySettingsFromCsvError,
  uploadCompanySettingsFromCsvSuccess,
} from './control-room.actions';

import { DateService } from '../../shared/services/date.service';
import { ControlType } from '../models/control-room-models';
import { ControlRoomService } from '../services/control-room.service';

@Injectable()
export class ControlRoomEffects {
  private readonly actions$ = inject(Actions);
  private readonly apiService = inject(ControlRoomService);
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly dateService = inject(DateService);
  private readonly store = inject(Store);

  getEnabledState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAutomaticFlowSchedulerEnabledState),
      mergeMap(({ payload: { type } }) =>
        this.apiService.getSchedulerEnabledState(type).pipe(
          map((enabled) => getAutomaticFlowSchedulerEnabledStateSuccess({ type, enabled })),
          catchError(() => of(getAutomaticFlowSchedulerEnabledStateError())),
        ),
      ),
    ),
  );

  triggerFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerAutomaticFlow),
      tap(({ payload: { type } }) =>
        this.dialog.notify({
          text: this.locale.translate(`controlRoom.${ControlType[type]}.startNowDescription`),
        }),
      ),
      mergeMap(({ payload: { type } }) =>
        this.apiService.triggerScheduler(type).pipe(
          map(() => triggerAutomaticFlowSuccess({ type })),
          catchError(() => of(triggerAutomaticFlowError({ type }))),
        ),
      ),
    ),
  );

  setEnabledState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setAutomaticFlowEnabledState),
      mergeMap(({ payload: { type, enabled } }) =>
        this.apiService.setEnabledStateScheduler(type, enabled).pipe(
          map((schedulerState) => {
            const descriptionKey = enabled ? 'activateDescription' : 'deactivateDescription';
            const translationKey = `controlRoom.${ControlType[type]}.${descriptionKey}`;
            this.dialog.notify({
              text: this.locale.translate(translationKey),
            });
            return setAutomaticFlowEnabledStateSuccess(schedulerState);
          }),
          catchError((_) => {
            this.dialog.notify({
              text: this.locale.translate('controlRoom.toggleFailedDescription', {
                type: ControlType[type],
              }),
            });
            return of(setAutomaticFlowEnabledStateError());
          }),
        ),
      ),
    ),
  );

  uploadCompanySettingsFromCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uploadCompanySettingsFromCsv),
      mergeMap(({ payload: { file } }) =>
        this.apiService.uploadCompanySettingsFromCsv(file).pipe(
          map((response) => uploadCompanySettingsFromCsvSuccess({ response })),
          catchError(() => of(uploadCompanySettingsFromCsvError())),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  uploadCompanySettingsFromCsvSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(uploadCompanySettingsFromCsvSuccess),
        tap(({ payload: { response } }) => {
          if (response.failedRecords.length > 0) {
            const failedRecordsList = response.failedRecords
              .map((record) => `<li>${record}</li>`)
              .join('');
            const failedRecordsHtml = `<ul>${failedRecordsList}</ul>`;
            this.dialog.confirm({
              title: this.locale.translate('controlRoom.csvUpload.dialog.failedTitle'),
              text: this.locale.translate('controlRoom.csvUpload.dialog.failedDescription', {
                failedRecords: failedRecordsHtml,
              }),
              cancelButton: false,
              pushHistoryState: false,
            });
          } else {
            this.dialog.notify({
              title: this.locale.translate('controlRoom.csvUpload.dialog.successTitle'),
              text: this.locale.translate('controlRoom.csvUpload.dialog.successDescription'),
            });
          }
        }),
      ),
    { dispatch: false },
  );

  showRecalculateBbaAdviceModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(showRecalculateBbaAdviceModal),
      exhaustMap(() =>
        this.dialog
          .confirm({
            title: this.locale.translate('controlRoom.recalculateBBA.title'),
            text: this.locale.translate('controlRoom.recalculateBBA.dialogDescription'),
            cancelButton: true,
            pushHistoryState: false,
          })
          .pipe(
            filter((action) => action.status === DIALOG_RETURN_TYPES.confirm),
            map(() => triggerRecalculateBbaAdvice()),
          ),
      ),
    ),
  );

  triggerBbaRecalculation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(triggerRecalculateBbaAdvice),
      switchMap(() =>
        this.apiService.triggerRecalculateBbaAdvice(this.dateService.thisYear()).pipe(
          map(() => triggerRecalculateBbaAdviceSuccess()),
          catchError((_) => of(triggerRecalculateBbaAdviceError())),
        ),
      ),
    ),
  );

  triggerRecalculateBbaAdviceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(triggerRecalculateBbaAdviceSuccess),
        tap(() => {
          this.dialog.notify({
            title: this.locale.translate('controlRoom.recalculateBBA.successTitle'),
            text: this.locale.translate('controlRoom.recalculateBBA.successDescription'),
          });
        }),
      ),
    { dispatch: false },
  );

  triggerRecalculateBbaAdviceError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(triggerRecalculateBbaAdviceError),
        tap(() => {
          this.dialog.notify({
            title: this.locale.translate('controlRoom.recalculateBBA.errorTitle'),
            text: this.locale.translate('controlRoom.recalculateBBA.errorDescription'),
          });
        }),
      ),
    { dispatch: false },
  );

  setAutomaticAdviceCustomersPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setAutomaticAdviceCustomersPreferences),
      switchMap(() =>
        this.apiService.setAutomaticAdviceCustomersPreferences().pipe(
          map(() => {
            this.store.dispatch(CoreActions.setLoading({ loading: false }));
            return setAutomaticAdviceCustomersPreferencesSuccess();
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(setAutomaticAdviceCustomersPreferencesError({ isNotFound: true }));
            }
            return of(setAutomaticAdviceCustomersPreferencesError());
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setAutomaticAdviceCustomersPreferencesSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setAutomaticAdviceCustomersPreferencesSuccess),
        tap(() => {
          this.dialog.notify({
            title: this.locale.translate(
              'controlRoom.automaticAdviceCustomersPreferences.successTitle',
            ),
            text: this.locale.translate(
              'controlRoom.automaticAdviceCustomersPreferences.successDescription',
            ),
          });
        }),
        map(() => getAutomaticAdviceCustomersPreferencesLastUpdate()),
      ),
    { dispatch: false },
  );

  setAutomaticAdviceCustomersPreferencesError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setAutomaticAdviceCustomersPreferencesError),
        tap((action) => {
          const isNotFound = action.payload?.isNotFound;

          this.dialog.notify({
            title: this.locale.translate(
              'controlRoom.automaticAdviceCustomersPreferences.errorTitle',
            ),
            text: isNotFound
              ? this.locale.translate(
                  'controlRoom.automaticAdviceCustomersPreferences.notFoundDescription',
                )
              : this.locale.translate(
                  'controlRoom.automaticAdviceCustomersPreferences.errorDescription',
                ),
          });
        }),
      ),
    { dispatch: false },
  );

  getAutomaticAdviceCustomersPreferencesLastUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAutomaticAdviceCustomersPreferencesLastUpdate),
      switchMap(() =>
        this.apiService.getAutomaticAdviceCustomersPreferencesLastUpdate().pipe(
          map((response) => getAutomaticAdviceCustomersPreferencesLastUpdateSuccess(response)),
          catchError((_) => of(getAutomaticAdviceCustomersPreferencesLastUpdateError())),
        ),
      ),
    ),
  );

  resetAutomaticAdviceForAllCustomers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetAutomaticAdviceForAllCustomers),
      exhaustMap(() =>
        this.dialog
          .confirm({
            title: this.locale.translate('controlRoom.resetAutomaticAdviceForAllCustomers.title'),
            text: this.locale.translate(
              'controlRoom.resetAutomaticAdviceForAllCustomers.dialogDescription',
            ),
            cancelButton: true,
            pushHistoryState: false,
          })
          .pipe(
            filter((action) => action.status === DIALOG_RETURN_TYPES.confirm),
            switchMap(() =>
              this.apiService.resetAutomaticAdviceForAllCustomers().pipe(
                map(() => resetAutomaticAdviceForAllCustomersSuccess()),
                catchError((_) => {
                  return of(resetAutomaticAdviceForAllCustomersError());
                }),
                startWith(CoreActions.setLoading({ loading: true })),
                finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
              ),
            ),
          ),
      ),
    ),
  );

  resetAutomaticAdviceForAllCustomersSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetAutomaticAdviceForAllCustomersSuccess),
        tap(() => {
          this.dialog.notify({
            title: this.locale.translate(
              'controlRoom.resetAutomaticAdviceForAllCustomers.successTitle',
            ),
            text: this.locale.translate(
              'controlRoom.resetAutomaticAdviceForAllCustomers.successDescription',
            ),
          });
        }),
      ),
    { dispatch: false },
  );

  resetAutomaticAdviceForAllCustomersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetAutomaticAdviceForAllCustomersError),
        tap(() => {
          this.dialog.notify({
            title: this.locale.translate(
              'controlRoom.resetAutomaticAdviceForAllCustomers.errorTitle',
            ),
            text: this.locale.translate(
              'controlRoom.resetAutomaticAdviceForAllCustomers.errorDescription',
            ),
          });
        }),
      ),
    { dispatch: false },
  );
}
